import clamp from 'lodash/clamp'
import Rectangle from '../../utils/Rectangle'
import Snapper from '../../utils/snapping'
import { cols, snapThreshold } from 'variables'

export const snapRound = value => {
  const snapped = Math.round(value)
  if (Math.abs(snapped - value) < snapThreshold)
    return { value: snapped, snapped: true }
  return { value, snapped: false }
}

// const widths = [2, 3, 4, 6].map(w => (35 - w + 1) / w)
// const isStandardLayout = (col, width) =>
//   // not too far right
//   width + col < 37 &&
//   // is standard width
//   widths.includes(width) &&
//   // is in a standard column
//   (col - 1) % (width + 1) === 0

const isStandardLayout = (col, width) =>
  // not too far right
  width + col < 37 &&
  // width 2, 5, 8 cols etc
  (width + 1) % 3 === 0 &&
  // left 1, 4, 7 cols etc
  (col - 1) % 3 === 0


function snapCols(layout, guides) {
  if (isStandardLayout(layout.left, layout.width)) {
    for (let left = layout.left; left < layout.width + layout.left; left += 3) {
      guides.push({ type: 'col', left })
    }
  }
}

/**
 * returns where the block should be placed
 * based on dragged size or click/hover position
 * and block aspect ratio
 * and snap to cols & sections
 */
export const snap = (
  { layout, aspect = null, action, sectionLayout, id = null },
  snapper
) => {
  const negative = layout.width < 0
  layout = new Rectangle(layout).positive()
  let guides = []

  // if dragging to the left, the right side sould be fixed
  if (!action.match(/-[ns]?w$/) === negative) {
    // resize|place-drag nw|w|sw
    // snap right to whole cols
    const { value, snapped } = snapRound(layout.right)
    if (snapped) {
      // snap width to whole cols
      const widthSnap = snapRound(layout.width)
      layout.width = widthSnap.value

      // min-width: 1, max-width 100%
      layout.width = clamp(layout.width, 1, cols)

      layout.x = value - layout.width
      guides.push({ type: 'vRule', left: value })
      if (widthSnap.snapped) {
        guides.push({ type: 'vRule', left: layout.left })
        snapCols(layout, guides)
      }
    }
  } else {
    // snap left to whole cols
    const { value, snapped } = snapRound(layout.left)
    if (snapped) {
      // snap width to whole cols
      const widthSnap = snapRound(layout.width)
      layout.width = widthSnap.value

      // min-width: 1, max-width 100%
      layout.width = clamp(layout.width, 1, cols)

      layout.x = value
      guides.push({ type: 'vRule', left: value })
      if (widthSnap.snapped) {
        guides.push({ type: 'vRule', left: layout.right })
        snapCols(layout, guides)
      }
    }
  }

  if (action === 'move' || action === 'place-click') {
    // if placing by click or moving an existing block - move layout to keep within board

    // can't overhang sides of board
    layout.x = clamp(layout.left, 0, cols - layout.width)
  } else {
    // action == resize | place-drag
    // if placing by drag or resizing - crop layout at edge of board

    // can't overhang sides of board
    layout.left = Math.max(layout.left, 0)
    layout.right = Math.min(layout.right, cols)
  }

  // if fixed aspect, set height based on width
  if (aspect) {
    if (action.match(/-n[ew]?$/)) {
      // resize|place-drag nw|n|ne
      const currentBottom = layout.bottom
      layout.height = layout.width * aspect
      layout.y = currentBottom - layout.height

      // reduce width if top would be off board
      if (layout.y < 0) {
        layout.top = 0
        layout.width = layout.height / aspect
      }
    } else {
      layout.height = aspect * layout.width
    }
  }

  // Snap to guides
  if ((action === 'move' && id) || action === 'place-click') {
    const snapped = snapper({ layout, id })
    layout = snapped.layout
    guides = guides.concat(snapped.guides)
  }

  // can't overhang top of board
  if (action === 'move' || action === 'place-click') {
    // if moving an existing block - move layout to keep within board
    layout.y = Math.max(layout.top, 0)
  } else {
    // if placing by drag or resizing - crop layout at edge of board
    // place-click handeled here too, but it's never overhanging top of board
    layout.top = Math.max(layout.top, 0)
  }

  for (const section of sectionLayout.values()) {
    const height = Math.max(section.height, section.minHeight)
    const sectionBottom = section.top + height

    // Last section
    if (section.number === sectionLayout.size - 1) {
      // if placing by click, prevent block being outside/below last section
      // so that you can reach the [+] button at the bottom

      // NOT anymore
      // if (action === 'place-click' && layout.bottom > sectionBottom) {
      //   const overlap = Math.min(layout.bottom - sectionBottom, layout.height)
      //   // don't move up so far that the block sticks out the top of the section
      //   layout.y -= Math.min(overlap, layout.top - section.top)
      // }

      // in other cases overhangs on the last section are dealt with by expanding it
    } else if (layout.top < sectionBottom && layout.bottom > sectionBottom) {
      // if layout crosses bottom
      if (action === 'move') {
        // if moving an existing block, snap into a section
        const overlap = layout.bottom - sectionBottom
        let offset
        if (overlap > layout.height / 2) {
          // rounding errors can cause glitches, add enough to ensure rounding behaves deterministically
          // TODO: determine a safe number of digits to round to and never deal with more precision than we need
          offset = layout.height - overlap + 0.0000000000001
        } else {
          // don't move up so far that the block sticks out the top of the section
          offset = Math.max(-overlap, section.top - layout.top)
        }
        layout.y += offset

        return {
          layout,
          // no horizontal guides when forced into a section, the will be wrong now
          guides: guides.filter(
            ({ type }) => type === 'vRule' || type === 'col'
          )
        }
      } else if (action === 'place-click') {
        // if placing by click, keep within current section
        const overlap = layout.bottom - sectionBottom
        // don't move up so far that the block sticks out the top of the section
        const offset = Math.max(-overlap, section.top - layout.top)
        layout.y += offset
        return {
          layout,
          // no horizontal guides when forced into a section, the will be wrong now
          guides: guides.filter(
            ({ type }) => type === 'vRule' || type === 'col'
          )
        }
      } else if (action.match(/-n[ew]?$/)) {
        // if resizing/dragging north - stop at top of section
        // resize|place-drag nw|n|ne
        layout.top = sectionBottom
      }
      // if placing by drag, or resizing the section will expand if needed
    }
  }

  return { layout, guides }
}

const snapper = new Snapper()

export const getSnapper = (layout, sections) => {
  // TODO make calcSnapPoints accept layout without transform
  const snapBlocks = {}
  Object.entries(layout).forEach(([id, layout]) => {
    snapBlocks[id] = { id, layout }
  })
  snapper.calcSnapPoints(snapBlocks, sections)
  return options => snap({sectionLayout: sections, ...options}, layout => snapper.getVerticalSnap(layout))
}
